import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebase';
import { collection, getDocs, doc, setDoc, deleteDoc, query, where, getDoc } from 'firebase/firestore';
import '../../styles/ClientDashboard.css';
import CustomerProgressVisuals from '../../components/CustomerProgressVisuals'; // <--- Imported Visuals Component
const ClientDashboard = () => {
  const [customerEmail, setCustomerEmail] = useState('');
  const [dietPlanName, setDietPlanName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [lifestyleModifications, setLifestyleModifications] = useState('');
const [supplements, setSupplements] = useState('');
  const [mealsGrid, setMealsGrid] = useState(Array(7).fill(null).map(() => Array(8).fill('')));
  const [notes, setNotes] = useState('');
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerUid, setSelectedCustomerUid] = useState(null);
  const [customerDietPlans, setCustomerDietPlans] = useState([]);
  
  const [editingPlan, setEditingPlan] = useState(null);
  const [currentStartDate, setCurrentStartDate] = useState(null);
  const [showSavedPlans, setShowSavedPlans] = useState(false);
  const [savedDietPlans, setSavedDietPlans] = useState([]);
  const [mealTimings, setMealTimings] = useState(Array(8).fill(''));
  const [selectedCustomerPlanStatus, setSelectedCustomerPlanStatus] = useState(null);
  const [selectedCustomerPlanEndDate, setSelectedCustomerPlanEndDate] = useState(null);
  const [globalTemplates, setGlobalTemplates] = useState([]);
  // Fetch customers and map their names
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const usersCollection = collection(db, 'users');
        const profilesCollection = collection(db, 'profiles');
        const usersSnapshot = await getDocs(usersCollection);
        const profilesSnapshot = await getDocs(profilesCollection);

        const profileNamesMap = {};
        profilesSnapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          profileNamesMap[docSnap.id] = data.name?.trim() || 'Unnamed';
        });

        const usersList = usersSnapshot.docs
          .filter((docSnap) => docSnap.data()?.role === 'customer')
          .map((docSnap) => {
            const userData = docSnap.data();
            const uid = docSnap.id;
            return {
              uid,
              name: profileNamesMap[uid] || 'Unknown Customer',
              email: userData.email || '',
              planStatus: userData.planStatus || 'active',
              planEndDate: userData.planEndDate || null,
            };
          });

        setCustomers(usersList);
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };

    fetchCustomers();
  }, []);

  // Fetch selected customer's plan status and end date
  useEffect(() => {
    const fetchSelectedCustomerPlanDetails = async () => {
      if (selectedCustomerUid) {
        try {
          const customerDocRef = doc(db, 'users', selectedCustomerUid);
          const customerDocSnap = await getDoc(customerDocRef);

          if (customerDocSnap.exists()) {
            const customerData = customerDocSnap.data();
            setSelectedCustomerPlanStatus(customerData.planStatus || 'active');
            setSelectedCustomerPlanEndDate(customerData.planEndDate || null);
          } else {
            setSelectedCustomerPlanStatus(null);
            setSelectedCustomerPlanEndDate(null);
          }
        } catch (error) {
          console.error('Error fetching selected customer plan details:', error);
          setSelectedCustomerPlanStatus(null);
          setSelectedCustomerPlanEndDate(null);
        }
      } else {
        setSelectedCustomerPlanStatus(null);
        setSelectedCustomerPlanEndDate(null);
      }
    };

    fetchSelectedCustomerPlanDetails();
  }, [selectedCustomerUid]);

  // Fetch all global diet templates
  useEffect(() => {
    const fetchGlobalTemplates = async () => {
      try {
        const templatesCollection = collection(db, 'globalDietTemplates');
        const querySnapshot = await getDocs(templatesCollection);
        const fetchedTemplates = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setGlobalTemplates(fetchedTemplates);
      } catch (error) {
        console.error('Error fetching global templates:', error);
      }
    };
    fetchGlobalTemplates();
  }, []);

  const handleCustomerChange = (e) => {
    const selectedUid = e.target.value;
    setSelectedCustomerUid(selectedUid);
    const selectedCustomer = customers.find((c) => c.uid === selectedUid);
    setCustomerEmail(selectedCustomer?.email || '');
    setCustomerDietPlans([]);
    setEditingPlan(null);
    setDietPlanName('');
    setStartDate('');
    setEndDate('');
    setMealsGrid(Array(7).fill(null).map(() => Array(8).fill('')));
    setNotes('');
    setCurrentStartDate(null);
    setShowSavedPlans(false);
    setSavedDietPlans([]);
    setMealTimings(Array(8).fill(''));
    setSelectedCustomerPlanStatus(null);
    setSelectedCustomerPlanEndDate(null);
  };

  // Fetch customer's diet plans
  useEffect(() => {
    const fetchCustomerDietPlans = async () => {
      if (selectedCustomerUid) {
        const dietPlansCollection = collection(db, 'users', selectedCustomerUid, 'dietPlans');
        const querySnapshot = await getDocs(dietPlansCollection);
        const plans = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setCustomerDietPlans(plans);
      } else {
        setCustomerDietPlans([]);
      }
      setEditingPlan(null);
    };
    fetchCustomerDietPlans();
  }, [selectedCustomerUid]);

  // Fetch saved diet plans
  const fetchSavedDietPlans = async () => {
    if (selectedCustomerUid && showSavedPlans) {
      const savedPlansCollection = collection(db, 'savedDietPlans');
      const q = query(savedPlansCollection, where('clientId', '==', selectedCustomerUid));
      const querySnapshot = await getDocs(q);
      const plans = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setSavedDietPlans(plans);
    } else {
      setSavedDietPlans([]);
    }
  };

  useEffect(() => {
    fetchSavedDietPlans();
  }, [selectedCustomerUid, showSavedPlans]);

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setCurrentStartDate(e.target.value ? new Date(e.target.value) : null);
  };

  const handleEndDateChange = (e) => setEndDate(e.target.value);

  const handleMealChange = (row, col, value) => {
    const newMealsGrid = [...mealsGrid];
    newMealsGrid[row][col] = value;
    setMealsGrid(newMealsGrid);
  };

  const handleMealTimingChange = (index, value) => {
    const newMealTimings = [...mealTimings];
    newMealTimings[index] = value;
    setMealTimings(newMealTimings);
  };

  const resetForm = () => {
    setDietPlanName('');
    setStartDate('');
    setEndDate('');
    setMealsGrid(Array(7).fill(null).map(() => Array(8).fill('')));
    setNotes('');
    setCurrentStartDate(null);
    setLifestyleModifications(''); // New
    setSupplements('');             // New
    setMealTimings(Array(8).fill(''));
    setEditingPlan(null);
    setUploadStatus(null);
    setUploadError(null);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setCustomerEmail(customers.find((c) => c.uid === selectedCustomerUid)?.email || '');
    setDietPlanName(plan.name);
    setStartDate(plan.startDate || '');
    setLifestyleModifications(plan.lifestyleModifications || ''); // New
    setSupplements(plan.supplements || '');
    setEndDate(plan.endDate || '');
    setMealsGrid(
      plan.weeklyDietPlan
        ? plan.weeklyDietPlan.map((dayPlan) => dayPlan.meals.map((meal) => `${meal}`))
        : Array(7).fill(null).map(() => Array(8).fill(''))
    );
    setNotes(plan.notes || '');
    setCurrentStartDate(plan.startDate ? new Date(plan.startDate) : null);
    setMealTimings(plan.mealTimings || Array(8).fill(''));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopyPlan = (plan) => {
    resetForm();
    setDietPlanName(`Copy of ${plan.name}`);
    setStartDate(plan.startDate || '');
    setEndDate(plan.endDate || '');
    setMealsGrid(
      plan.weeklyDietPlan
        ? plan.weeklyDietPlan.map((dayPlan) => [...dayPlan.meals])
        : Array(7).fill(null).map(() => Array(8).fill(''))
    );
    setNotes(plan.notes || '');
    setCurrentStartDate(plan.startDate ? new Date(plan.startDate) : null);
    setMealTimings(plan.mealTimings ? [...plan.mealTimings] : Array(8).fill(''));
    setEditingPlan(null);
    setUploadStatus('Plan copied to form. Modify and upload as a new plan.');
    setUploadError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Apply a selected global template
  const handleApplyTemplate = (templateId) => {
    const template = globalTemplates.find((t) => t.id === templateId);

    if (template) {
      resetForm();
      setDietPlanName(`[Template] ${template.name}`);

      const loadedMealsGrid = template.weeklyDietPlan
        ? template.weeklyDietPlan.map((dayPlan) => [...dayPlan.meals])
        : Array(7).fill(null).map(() => Array(8).fill(''));

      setMealsGrid(loadedMealsGrid);
      setNotes(template.notes || '');
      setMealTimings(template.mealTimings ? [...template.mealTimings] : Array(8).fill(''));
      setEditingPlan(null);
      setUploadStatus(`Template "${template.name}" loaded into form. Please set dates and customer details.`);
      setUploadError(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setUploadError('Template not found.');
      setTimeout(() => setUploadError(null), 3000);
    }
  };

  const handleDeletePlan = async (customerUid, planId) => {
    if (window.confirm('Are you sure you want to delete this diet plan?')) {
      try {
        const dietPlanDocRef = doc(db, 'users', customerUid, 'dietPlans', planId);
        await deleteDoc(dietPlanDocRef);
        const updatedPlans = customerDietPlans.filter((plan) => plan.id !== planId);
        setCustomerDietPlans(updatedPlans);
        setUploadStatus('Diet plan deleted successfully!');
        setTimeout(() => setUploadStatus(null), 3000);
      } catch (error) {
        console.error('Error deleting diet plan:', error);
        setUploadError('Failed to delete diet plan.');
        setTimeout(() => setUploadError(null), 3000);
      }
    }
  };

  const handleSaveDietPlan = async (planToSave) => {
    if (!selectedCustomerUid) {
      setUploadError('Please select a customer first to save a plan.');
      return;
    }

    const savedPlansCollection = collection(db, 'savedDietPlans');
    const planData = { ...planToSave, clientId: selectedCustomerUid, savedAt: new Date(), mealTimings };

    try {
      await setDoc(doc(savedPlansCollection), planData);
      setUploadStatus(`Diet plan "${planToSave.name}" saved successfully!`);
      if (showSavedPlans) fetchSavedDietPlans();
      setTimeout(() => setUploadStatus(null), 3000);
    } catch (error) {
      console.error('Error saving diet plan:', error);
      setUploadError('Failed to save diet plan.');
      setTimeout(() => setUploadError(null), 3000);
    }
  };

  const handleToggleSavedPlans = () => setShowSavedPlans(!showSavedPlans);

  const handleUploadDiet = async (e) => {
    e.preventDefault();
    setUploadStatus('Uploading...');
    setUploadError(null);

    try {
      if (!customerEmail) {
        setUploadError('Please select a customer.');
        setUploadStatus(null);
        return;
      }

      const selectedCustomer = customers.find((customer) => customer.email === customerEmail);
      if (!selectedCustomer) {
        setUploadError('Selected customer not found.');
        setUploadStatus(null);
        return;
      }

      const customerUid = selectedCustomer.uid;

      if (selectedCustomerPlanStatus === 'paused') {
        setUploadError('Cannot upload diet plan: Customer plan is currently paused.');
        setUploadStatus(null);
        return;
      }

      if (selectedCustomerPlanEndDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const planEndDateObj = new Date(selectedCustomerPlanEndDate);
        planEndDateObj.setHours(0, 0, 0, 0);

        if (today > planEndDateObj) {
          setUploadError('Cannot upload diet plan: Customer plan has expired.');
          setUploadStatus(null);
          return;
        }
      }

      const dietPlanId = editingPlan?.id || doc(collection(db, 'users', customerUid, 'dietPlans')).id;
      const dietPlanDocRef = doc(db, 'users', customerUid, 'dietPlans', dietPlanId);

      const weeklyDietPlan = Array(7)
        .fill(null)
        .map((_, dayIndex) => {
          const dailyMeals = Array.from({ length: 8 }, (_, mealIndex) => mealsGrid[dayIndex][mealIndex] || '');
          const currentDate = currentStartDate ? new Date(currentStartDate) : new Date();
          currentDate.setDate(currentDate.getDate() + dayIndex);
          const formattedDate = currentDate.toISOString().split('T')[0];
          return { date: formattedDate, meals: dailyMeals };
        });

      const dietPlanData = {
        name: dietPlanName,
        startDate: startDate,
        endDate: endDate,
        weeklyDietPlan,
        notes: notes,
        mealTimings: mealTimings,
        uploadedAt: editingPlan?.uploadedAt || new Date(),
        lastModified: new Date(),
      };

      await setDoc(dietPlanDocRef, dietPlanData, { merge: true });

      setUploadStatus(`Diet plan ${editingPlan ? 'updated' : 'uploaded'} successfully!`);
      resetForm();
      const updatedPlansSnapshot = await getDocs(collection(db, 'users', customerUid, 'dietPlans'));
      setCustomerDietPlans(updatedPlansSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setTimeout(() => setUploadStatus(null), 3000);
    } catch (error) {
      console.error(`Error ${editingPlan ? 'updating' : 'uploading'} diet plan:`, error);
      setUploadError(`Failed to ${editingPlan ? 'update' : 'upload'} diet plan. Error: ${error.message}`);
      setUploadStatus(null);
      setTimeout(() => setUploadError(null), 5000);
    }
  };

  const getDatesArray = () => {
    if (!currentStartDate) return Array(7).fill('Date');
    return Array(7)
      .fill(null)
      .map((_, index) => {
        const date = new Date(currentStartDate);
        date.setDate(date.getDate() + index);
        return date.toLocaleDateString();
      });
  };

  return (
    <div className="dashboard-container client-dashboard">
      <h1>My Dashboard</h1>
      <div className="dashboard-content">
        <div>
          <label htmlFor="customerEmail">Select Customer:</label>
          <select
            id="customerUid"
            value={selectedCustomerUid}
            onChange={handleCustomerChange}
            required
          >
            <option value="">-- Select a Customer --</option>
            {customers.length > 0 ? (
              customers.map((customer) => (
                <option key={customer.uid} value={customer.uid}>
                  {customer.name}
                </option>
              ))
            ) : (
              <option disabled>No customers found</option>
            )}
          </select>
        </div>

        {selectedCustomerUid && (
          <div className="customer-plan-status-info">
            {selectedCustomerPlanStatus && (
              <p>
                <strong>Customer Plan Status:</strong> {selectedCustomerPlanStatus.toUpperCase()}
              </p>
            )}
            {selectedCustomerPlanEndDate && (
              <p>
                <strong>Plan End Date:</strong> {new Date(selectedCustomerPlanEndDate).toLocaleDateString()}
                {selectedCustomerPlanEndDate &&
                  new Date() > new Date(selectedCustomerPlanEndDate) && (
                    <span style={{ color: 'red', marginLeft: '10px' }}> (EXPIRED!)</span>
                  )}
              </p>
            )}
            {selectedCustomerPlanStatus === 'paused' && (
              <p style={{ color: 'orange', fontWeight: 'bold' }}>
                This customer's plan is PAUSED. You cannot upload a diet plan until it's unpaused.
              </p>
            )}
            {selectedCustomerPlanEndDate &&
              new Date() > new Date(selectedCustomerPlanEndDate) && (
                <p style={{ color: 'red', fontWeight: 'bold' }}>
                  This customer's plan has EXPIRED. You cannot upload a diet plan until the end date is updated.
                </p>
              )}
          </div>
        )}

        {/* Place the new visuals component here */}
        {selectedCustomerUid && <CustomerProgressVisuals customerUid={selectedCustomerUid} />}
        {/* Horizontal rule for visual separation */}
        <hr className="visuals-separator" />

        <h2>{editingPlan ? 'Edit Diet Plan' : 'Upload Weekly Diet Plan for Customer'}</h2>
        <form onSubmit={handleUploadDiet} className="diet-plan-form">
          {/* Template Selector */}
          <div className="template-select-container">
            <label htmlFor="templateSelector">Apply Template (Optional):</label>
            <select
              id="templateSelector"
              onChange={(e) => {
                if (e.target.value) handleApplyTemplate(e.target.value);
                e.target.value = '';
              }}
              value=""
              disabled={globalTemplates.length === 0}
            >
              <option value="">-- Select a Global Template to Load --</option>
              {globalTemplates.length > 0 ? (
                globalTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))
              ) : (
                <option disabled>No global templates found</option>
              )}
            </select>
          </div>

          <div>
            <label htmlFor="dietPlanName">Diet Plan Name:</label>
            <input
              type="text"
              id="dietPlanName"
              value={dietPlanName}
              onChange={(e) => setDietPlanName(e.target.value)}
              required
            />
          </div>

          <div className="date-range">
            <div>
              <label htmlFor="startDate">Start Date:</label>
              <input type="date" id="startDate" value={startDate} onChange={handleStartDateChange} />
            </div>
            <div>
              <label htmlFor="endDate">End Date:</label>
              <input type="date" id="endDate" value={endDate} onChange={handleEndDateChange} />
            </div>
          </div>

          {/* Weekly meals grid */}
          <div className="weekly-meals-grid">
            <h3>Weekly Meal Plan</h3>
            <table className="meals-table">
              <thead>
                <tr>
                  <th>Date</th>
                  {Array.from({ length: 8 }, (_, index) => (
                    <th key={index}>
                      <input
                        type="text"
                        placeholder={`Meal ${index + 1} Timing`}
                        value={mealTimings[index]}
                        onChange={(e) => handleMealTimingChange(index, e.target.value)}
                        className="timing-input"
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array(7)
                  .fill(null)
                  .map((_, rowIndex) => (
                    <tr key={rowIndex}>
                      <th>{currentStartDate ? getDatesArray()[rowIndex] : `Day ${rowIndex + 1}`}</th>
                      {Array.from({ length: 8 }, (_, colIndex) => (
                        <td key={colIndex}>
                          <input
                            type="text"
                            value={mealsGrid[rowIndex][colIndex]}
                            onChange={(e) => handleMealChange(rowIndex, colIndex, e.target.value)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
{/* --- Extra Guidelines Section --- */}
<div className="diet-plan-extra-info">
    <div className="form-group">
        <label htmlFor="notes">📝 General Notes:</label>
        <textarea 
            id="notes" 
            value={notes} 
            onChange={(e) => setNotes(e.target.value)} 
            rows="3" 
            placeholder="General advice or warnings..."
        />
    </div>

    <div className="form-group">
        <label htmlFor="lifestyle">🏃 Lifestyle Modifications:</label>
        <textarea 
            id="lifestyle" 
            value={lifestyleModifications} 
            onChange={(e) => setLifestyleModifications(e.target.value)} 
            rows="3" 
            placeholder="Steps, sleep, hydration, or exercise tips..."
        />
    </div>

    <div className="form-group">
        <label htmlFor="supplements">💊 Supplements:</label>
        <textarea 
            id="supplements" 
            value={supplements} 
            onChange={(e) => setSupplements(e.target.value)} 
            rows="3" 
            placeholder="Vitamin dosages, timing, etc..."
        />
    </div>
</div>

          <button
            type="submit"
            disabled={
              selectedCustomerPlanStatus === 'paused' ||
              (selectedCustomerPlanEndDate && new Date() > new Date(selectedCustomerPlanEndDate))
            }
          >
            {editingPlan ? 'Update Diet Plan' : 'Upload Diet Plan'}
          </button>

          {uploadStatus && <p className="upload-status">{uploadStatus}</p>}
          {uploadError && <p className="upload-error">{uploadError}</p>}
        </form>

        {selectedCustomerUid && customerDietPlans.length > 0 && (
          <div className="existing-diet-plans">
            <h3>
              Existing Diet Plans for {customers.find((c) => c.uid === selectedCustomerUid)?.name}
            </h3>
            <ul>
              {customerDietPlans.map((plan) => (
                <li key={plan.id}>
                  {plan.name} ({plan.startDate} - {plan.endDate})
                  <button onClick={() => handleEditPlan(plan)}>Edit</button>
                  <button onClick={() => handleCopyPlan(plan)}>Copy</button>
                  <button onClick={() => handleDeletePlan(selectedCustomerUid, plan.id)}>Delete</button>
                  <button onClick={() => handleSaveDietPlan(plan)}>Save</button>
                </li>
              ))}
            </ul>
            

          </div>
          
        )}
        

        {selectedCustomerUid && (
          <div className="saved-diet-plans">
            <h3>
              Saved Diet Plans for {customers.find((c) => c.uid === selectedCustomerUid)?.name}
              <button type="button" onClick={handleToggleSavedPlans}>
                {showSavedPlans ? 'Hide Saved Plans' : 'Show Saved Plans'}
              </button>
            </h3>
            {showSavedPlans && savedDietPlans.length > 0 ? (
              <ul>
                {savedDietPlans.map((plan) => (
                  <li key={plan.id}>
                    {plan.name} (Saved At:{' '}
                    {plan.savedAt?.toDate?.()?.toLocaleString() ||
                      new Date(plan.savedAt).toLocaleString()}
                    )
                    <button onClick={() => handleCopyPlan(plan)}>Copy</button>
                  </li>
                ))}
              </ul>
            ) : showSavedPlans ? (
              <p>No saved diet plans found.</p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDashboard;
